import { ref, computed, onUnmounted } from 'vue'

const STORAGE_KEYS = {
  VISIT_COUNT: 'bsg_visit_count',
  VISITED_IDS: 'bsg_visited_ids', // Unique bond IDs visited (avoid counting duplicate tabs)
  FORCED: 'bsg_forced',
  COOLDOWN_START: 'bsg_cooldown_start',
}

const BCAST_CHANNEL = 'equirize-bond-signup-gate'
const VISIT_THRESHOLD = 7
const COOLDOWN_MS = 4 * 60 * 60 * 1000 // 4 hours
const POPUP_INTERVAL_MS = 60 * 1000 // 60 seconds

const get = (key) => {
  if (typeof globalThis.window === 'undefined') return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const set = (key, value) => {
  if (typeof globalThis.window === 'undefined') return
  try {
    localStorage.setItem(key, String(value))
  } catch {}
}

const remove = (key) => {
  if (typeof globalThis.window === 'undefined') return
  try {
    localStorage.removeItem(key)
  } catch {}
}

/**
 * Signup gating for Bond Details pages.
 * - 60s timer: show closable popup every 60 seconds
 * - visits >= 5: forced modal, 4hr cooldown, block access
 * - Cooldown resets after 4 hours (visits=0, forced=false)
 */
export function useBondSignupGate() {
  const showPopup = ref(false)
  const forced = ref(false)
  const isClosable = ref(true) // Explicit: true for 60s popup, false for forced
  const popupKey = ref(0) // Increment each show to force dialog re-mount with correct closable state
  let popupInterval = null
  let broadcastChannel = null

  const visitCount = computed(() => {
    const visitedRaw = get(STORAGE_KEYS.VISITED_IDS)
    if (!visitedRaw) return 0
    try {
      const visited = JSON.parse(visitedRaw)
      return Array.isArray(visited) ? visited.length : 0
    } catch {
      return 0
    }
  })

  const cooldownStart = computed(() => {
    const ts = get(STORAGE_KEYS.COOLDOWN_START)
    return ts ? Number.parseInt(ts, 10) : null
  })

  /**
   * Check if cooldown has expired and reset state.
   */
  function resetAfterCooldown() {
    const start = cooldownStart.value
    if (!start) return
    if (Date.now() - start >= COOLDOWN_MS) {
      remove(STORAGE_KEYS.VISIT_COUNT)
      remove(STORAGE_KEYS.VISITED_IDS)
      remove(STORAGE_KEYS.FORCED)
      remove(STORAGE_KEYS.COOLDOWN_START)
      forced.value = false
      isClosable.value = true
      showPopup.value = false
      broadcast('reset')
    }
  }

  /**
   * Whether forced signup is active (visits >= 5, cooldown not expired).
   */
  function isForced() {
    resetAfterCooldown()
    const f = get(STORAGE_KEYS.FORCED)
    if (f !== 'true') return false
    const start = cooldownStart.value
    if (!start || Date.now() - start >= COOLDOWN_MS) return false
    return true
  }

  /**
   * Whether user is blocked from bond details (during cooldown).
   */
  function isBlocked() {
    return isForced()
  }

  function broadcast(type) {
    if (typeof BroadcastChannel === 'undefined') return
    try {
      const ch = broadcastChannel || new BroadcastChannel(BCAST_CHANNEL)
      ch.postMessage({ type, forced: isForced(), timestamp: Date.now() })
      if (!broadcastChannel) broadcastChannel = ch
    } catch {}
  }

  function setupBroadcast() {
    if (typeof BroadcastChannel === 'undefined') return
    try {
      broadcastChannel = new BroadcastChannel(BCAST_CHANNEL)
      broadcastChannel.onmessage = (e) => {
        if (e.data?.type === 'forced' && e.data?.forced) {
          forced.value = true
          isClosable.value = false
          showPopup.value = true
        } else if (e.data?.type === 'reset') {
          forced.value = false
          isClosable.value = true
          showPopup.value = false
        }
      }
    } catch {}
  }

  /**
   * Increment visit count for unique bond pages only. Duplicate tabs (same bond) don't count.
   * @param {string} bondId - Route param id (bond/segment ID)
   */
  function incrementVisit(bondId) {
    if (typeof globalThis.window === 'undefined') return
    resetAfterCooldown()
    const id = bondId || 'unknown'
    const visitedRaw = get(STORAGE_KEYS.VISITED_IDS)
    const visited = visitedRaw ? JSON.parse(visitedRaw) : []
    if (visited.includes(id)) return // Already counted this bond - skip (duplicate tab)
    visited.push(id)
    set(STORAGE_KEYS.VISITED_IDS, JSON.stringify(visited))
    const count = visited.length
    set(STORAGE_KEYS.VISIT_COUNT, count)
    if (count >= VISIT_THRESHOLD) {
      set(STORAGE_KEYS.FORCED, 'true')
      set(STORAGE_KEYS.COOLDOWN_START, Date.now().toString())
      forced.value = true
      isClosable.value = false
      showPopup.value = true
      broadcast('forced')
    }
  }

  /**
   * Start popup timer. Calls callback every 60 seconds.
   * Only shows closable popup when not forced.
   */
  function startPopupTimer(callback) {
    if (typeof globalThis.window === 'undefined') return
    if (popupInterval) clearInterval(popupInterval)
    popupInterval = setInterval(() => {
      resetAfterCooldown()
      if (isForced()) {
        forced.value = true
        isClosable.value = false
        showPopup.value = true
      } else if (callback) {
        isClosable.value = true
        popupKey.value += 1
        callback()
      }
    }, POPUP_INTERVAL_MS)
  }

  function stopPopupTimer() {
    if (popupInterval) {
      clearInterval(popupInterval)
      popupInterval = null
    }
  }

  function openClosablePopup() {
    if (!isForced()) {
      forced.value = false
      isClosable.value = true
      showPopup.value = true
    }
  }

  function closePopup() {
    if (!isForced()) {
      showPopup.value = false
    }
  }

  onUnmounted(() => {
    stopPopupTimer()
    if (broadcastChannel) {
      broadcastChannel.close()
      broadcastChannel = null
    }
  })

  return {
    showPopup,
    forced,
    isClosable,
    popupKey,
    visitCount,
    incrementVisit,
    startPopupTimer,
    isForced,
    isBlocked,
    resetAfterCooldown,
    setupBroadcast,
    openClosablePopup,
    closePopup,
    stopPopupTimer,
  }
}
